import React, { useState } from 'react';
import { useMutation, type DocumentNode } from '@apollo/client';

/**
 * "Recently deleted" (pages and articles): what was deleted in the last 30
 * days, each with a Restore button. A restored item comes back as a draft at
 * its old address; the server refuses (and says why) if the address is taken.
 * Nothing renders when the list is empty.
 */

export interface DeletedItem {
  id: string;
  title: string;
  path: string | null;
  deletedAt: string;
  daysLeft: number;
}

export function RecentlyDeleted({
  accountId,
  items,
  restoreMutation,
  onRestored,
  note,
  variant = 'rail',
}: {
  accountId: string;
  items: DeletedItem[];
  restoreMutation: DocumentNode;
  onRestored: (id: string) => void;
  note: string;
  /** rail: the Pages side list; list: under the article list. */
  variant?: 'rail' | 'list';
}) {
  const [restore] = useMutation(restoreMutation);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!items.length) return null;
  const rail = variant === 'rail';
  return (
    <div style={rail ? undefined : { margin: '8px 0 32px', padding: '0 12px' }}>
      <div
        className={rail ? 'pg-rail-head' : undefined}
        style={
          rail
            ? undefined
            : {
                font: '600 11px var(--ink-font)',
                letterSpacing: '0.08em',
                color: 'var(--ink-ink3)',
                margin: '0 0 6px',
              }
        }
      >
        RECENTLY DELETED
      </div>
      {items.map((d) => (
        <div
          key={d.id}
          className={rail ? 'pg-rail-item' : undefined}
          style={
            rail
              ? { cursor: 'default' }
              : {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderTop: '1px solid var(--ink-line)',
                }
          }
        >
          <span
            className={rail ? 'pg-rail-text' : undefined}
            style={rail ? undefined : { flex: 1, minWidth: 0 }}
          >
            <span
              className={rail ? 'pg-rail-title' : undefined}
              style={{ color: 'var(--ink-ink3)', display: rail ? undefined : 'block' }}
            >
              {d.title || 'Untitled'}
            </span>
            <span
              className={rail ? 'pg-rail-path' : undefined}
              style={rail ? undefined : { fontSize: 12, color: 'var(--ink-ink3)' }}
            >
              {d.path ? `${d.path} · ` : ''}
              {d.daysLeft} day{d.daysLeft === 1 ? '' : 's'} left to restore
            </span>
          </span>
          <button
            type="button"
            className="ink-btn"
            style={{ padding: '2px 10px', fontSize: 12 }}
            disabled={busy === d.id}
            onClick={async () => {
              setBusy(d.id);
              setError(null);
              try {
                await restore({ variables: { accountId, id: d.id } });
                onRestored(d.id);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Couldn’t restore it');
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy === d.id ? 'Restoring…' : 'Restore'}
          </button>
        </div>
      ))}
      {error ? (
        <p role="alert" style={{ color: 'var(--ink-red)', fontSize: 12, margin: '6px 12px' }}>
          {error}
        </p>
      ) : null}
      <p className="pg-note" style={{ margin: rail ? '6px 12px' : '6px 0', fontSize: 12 }}>
        {note}
      </p>
    </div>
  );
}
